import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Filter, Inbox, Paperclip, X } from "lucide-react";

import {
  useClassifyInbound,
  useInboundMessages,
  useLinkInboundRfq,
  useRfqs,
} from "@/api/queries";
import type { InboundClassification, InboundMessage } from "@/api/types";
import { Button } from "@/components/Button";
import { Dialog } from "@/components/Dialog";
import { Pagination, TableShell, Td, Th, Tr } from "@/components/DataTable";
import { Field, Select } from "@/components/Input";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/components/toast-context";
import { Page } from "@/layout/Page";
import { PageHeader } from "@/layout/PageHeader";
import {
  formatDateTime,
  formatInboundClassification,
  formatLinkSource,
  pluralize,
} from "@/lib/format";

const PAGE_SIZE = 50;

const CLASSIFICATIONS: InboundClassification[] = [
  "UNCLASSIFIED",
  "QUOTE",
  "INVOICE",
  "CONFIRMATION",
  "OTHER",
];

/**
 * Ce qui est arrivé (V1.6-05).
 *
 * **De la donnée.** Un message reçu ne crée rien : pas de soumission, pas de
 * bon de commande, pas de prix, pas de mouvement de stock. Cet écran le range,
 * le montre, et laisse quelqu'un dire ce que c'est et à quoi ça se rapporte.
 *
 * Il n'y a donc aucun bouton « créer la commande » ni « répondre » : ces
 * gestes n'existent pas encore, et en dessiner l'illusion serait pire que leur
 * absence.
 */
export function IncomingMessagesPage() {
  const [params, setParams] = useSearchParams();
  const [reading, setReading] = useState<InboundMessage | null>(null);

  const classification = params.get("classification") ?? undefined;
  const offset = Number(params.get("offset") ?? 0);

  const query = useMemo(
    () => ({
      classification: classification as InboundClassification | undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    [classification, offset],
  );

  const { data, isLoading, error, refetch } = useInboundMessages(query);

  function update(next: Record<string, string | undefined>) {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value === undefined || value === "") merged.delete(key);
      else merged.set(key, value);
    }
    if (!("offset" in next)) merged.delete("offset");
    setParams(merged, { replace: true });
  }

  return (
    <Page>
      <PageHeader
        title="Messages reçus"
        subtitle="Ce qui est arrivé, tel quel. Un message reçu ne déclenche rien."
      />

      <div className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-surface">
        <div className="flex shrink-0 flex-col gap-2 border-b border-border p-3 sm:flex-row sm:items-center">
          <Select
            value={classification ?? ""}
            onChange={(event) => update({ classification: event.target.value || undefined })}
            aria-label="Filtrer par classement"
            className="sm:w-64"
          >
            <option value="">Tous les classements</option>
            {CLASSIFICATIONS.map((value) => (
              <option key={value} value={value}>
                {formatInboundClassification(value)}
              </option>
            ))}
          </Select>
          {classification && (
            <button
              type="button"
              onClick={() => setParams(new URLSearchParams(), { replace: true })}
              className="flex h-10 items-center gap-1.5 rounded-md px-2 text-[13px] text-ink-subtle hover:text-ink"
            >
              <X className="size-4" aria-hidden />
              Effacer
            </button>
          )}
        </div>

        <div className="min-w-0">
          {isLoading && <TableSkeleton rows={8} columns={5} />}
          {error && <ErrorState error={error} onRetry={() => void refetch()} />}

          {data && data.items.length === 0 && (
            <EmptyState
              icon={
                classification ? (
                  <Filter className="size-6" aria-hidden />
                ) : (
                  <Inbox className="size-6" aria-hidden />
                )
              }
              title={classification ? "Aucun message ne correspond" : "Aucun message reçu"}
              description={
                classification
                  ? "Essayez un autre classement."
                  : "Les réponses des fournisseurs apparaîtront ici."
              }
            />
          )}

          {data && data.items.length > 0 && (
            <>
              <div className="hidden md:block">
                <TableShell>
                  <thead>
                    <tr>
                      <Th className="whitespace-nowrap">Reçu le</Th>
                      <Th className="whitespace-nowrap">Expéditeur</Th>
                      <Th className="max-w-0">Sujet</Th>
                      <Th className="whitespace-nowrap">Demande</Th>
                      <Th>Classement</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((item) => (
                      <InboundRow key={item.id} item={item} onRead={setReading} />
                    ))}
                  </tbody>
                </TableShell>
              </div>

              <ul className="divide-y divide-border/60 md:hidden">
                {data.items.map((item) => (
                  <li key={item.id}>
                    <InboundCard item={item} onRead={setReading} />
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {data && data.total > PAGE_SIZE && (
          <Pagination
            offset={offset}
            limit={PAGE_SIZE}
            total={data.total}
            onChange={(next) => update({ offset: String(next) })}
          />
        )}
      </div>

      <InboundDialog message={reading} onClose={() => setReading(null)} />
    </Page>
  );
}

function InboundRow({
  item,
  onRead,
}: {
  item: InboundMessage;
  onRead: (message: InboundMessage) => void;
}) {
  return (
    <Tr>
      <Td className="whitespace-nowrap text-[12px] text-ink-muted">
        {formatDateTime(item.received_at)}
      </Td>
      <Td className="whitespace-nowrap text-[13px] text-ink">{item.from_email}</Td>
      <Td className="max-w-0">
        <button
          type="button"
          onClick={() => onRead(item)}
          className="flex min-w-0 items-center gap-1.5 text-left hover:text-brand"
        >
          <span className="min-w-0 truncate text-ink">{item.subject || "(sans objet)"}</span>
          {item.attachments.length > 0 && (
            <Paperclip className="size-3.5 shrink-0 text-ink-subtle" aria-hidden />
          )}
        </button>
      </Td>
      <Td className="whitespace-nowrap font-mono text-[12px] text-ink-muted">
        {item.rfq_id ? (
          <Link to={`/rfqs/${item.rfq_id}`} className="hover:text-brand">
            {item.rfq_code ?? "—"}
          </Link>
        ) : (
          "—"
        )}
      </Td>
      <Td>
        <StatusBadge tone={item.classified_as === "UNCLASSIFIED" ? "unknown" : "info"}>
          {formatInboundClassification(item.classified_as)}
        </StatusBadge>
      </Td>
    </Tr>
  );
}

function InboundCard({
  item,
  onRead,
}: {
  item: InboundMessage;
  onRead: (message: InboundMessage) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onRead(item)}
      className="flex w-full min-h-[64px] items-center gap-3 px-3 py-3 text-left active:bg-surface-hover"
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] font-medium text-ink">
          {item.subject || "(sans objet)"}
        </p>
        <p className="truncate text-[12px] text-ink-muted">{item.from_email}</p>
        <p className="mt-0.5 text-[11px] text-ink-subtle">
          {formatDateTime(item.received_at)}
          {item.rfq_code ? ` · ${item.rfq_code}` : ""}
        </p>
      </div>
      <StatusBadge
        tone={item.classified_as === "UNCLASSIFIED" ? "unknown" : "info"}
        className="shrink-0"
      >
        {formatInboundClassification(item.classified_as)}
      </StatusBadge>
    </button>
  );
}

/**
 * Le message reçu, en lecture seule, et les deux seules actions humaines.
 *
 * Le corps affiché est le **texte** : un HTML d'origine inconnue n'est jamais
 * injecté dans la page, et le backend ne le renvoie même pas.
 */
function InboundDialog({
  message,
  onClose,
}: {
  message: InboundMessage | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const classify = useClassifyInbound();
  const link = useLinkInboundRfq();
  const { data: rfqs } = useRfqs({ limit: 100 }, { enabled: message !== null });

  async function setClassification(value: InboundClassification) {
    if (!message) return;
    try {
      await classify.mutateAsync({ messageId: message.id, classification: value });
      toast.success("Classement enregistré");
    } catch (failure) {
      toast.error("Classement non enregistré", String(failure));
    }
  }

  async function setLink(value: string) {
    if (!message) return;
    try {
      await link.mutateAsync({ messageId: message.id, rfq_id: value || null });
      toast.success(value ? "Message rattaché" : "Message détaché");
    } catch (failure) {
      toast.error("Rattachement non enregistré", String(failure));
    }
  }

  return (
    <Dialog
      open={message !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={message?.subject || "(sans objet)"}
      description={message ? `De ${message.from_email}` : ""}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Fermer
        </Button>
      }
    >
      {message && (
        <div className="flex flex-col gap-3 text-[13px]">
          <p className="text-ink-muted">
            Reçu le {formatDateTime(message.received_at)} · {formatLinkSource(message.link_source)}
          </p>

          <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded-md border border-border bg-surface-raised p-3 text-[12px] text-ink-muted">
            {message.body_text || "(message vide)"}
          </pre>

          {message.has_html && (
            <p className="text-[12px] text-ink-subtle">
              Ce message contenait une version HTML. HD-Vault affiche le texte : le HTML reçu
              n'est jamais rendu dans la page.
            </p>
          )}

          {message.attachments.length > 0 && (
            <p className="flex items-center gap-1.5 text-ink-muted">
              <Paperclip className="size-3.5 shrink-0" aria-hidden />
              {pluralize(message.attachments.length, "pièce jointe")}
            </p>
          )}

          <Field label="Classement" hint="Une étiquette. Elle ne crée aucune soumission.">
            <Select
              value={message.classified_as}
              onChange={(event) =>
                void setClassification(event.target.value as InboundClassification)
              }
              aria-label="Classement du message"
            >
              {CLASSIFICATIONS.map((value) => (
                <option key={value} value={value}>
                  {formatInboundClassification(value)}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Demande de prix liée" hint="Un repère. La demande ne change pas.">
            <Select
              value={message.rfq_id ?? ""}
              onChange={(event) => void setLink(event.target.value)}
              aria-label="Demande de prix liée"
            >
              <option value="">Aucune</option>
              {rfqs?.items.map((rfq) => (
                <option key={rfq.id} value={rfq.id}>
                  {rfq.code} — {rfq.title}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      )}
    </Dialog>
  );
}
